import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Pagination from '../../utils/pagination';
import { getApi, deleteApi } from '../../utils/api/api';
import { showToast } from '../../utils/toast';
import { showConfirmationAlert } from '../../utils/confirmationAlert';
import { useLanguage } from '../../contexts/LanguageContext';
import Loader from '../../utils/loader';

const AdminBlogsList = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [newsData, setNewsData] = useState({
    data: [],
    total: 0,
    per_page: 9,
    current_page: 1,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const baseUrl = "https://backend.tokoyarn.com/public/";

  const fetchNews = async (page = 1) => {
    setLoading(true);
    try {
      const response = await getApi(`admin/news?page=${page}`);
      if (response.data && response.data.news) {
        setNewsData({
          data: response.data.news.data,
          total: response.data.news.total,
          per_page: response.data.news.per_page || 9,
          current_page: response.data.news.current_page,
        });
      } else {
        setNewsData({ data: [], total: 0, per_page: 9, current_page: 1 });
      }
    } catch (error) {
      showToast('error', error.message || 'Failed to fetch news.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    showConfirmationAlert({
      message: t('adminNews.confirmDelete', 'Are you sure you want to delete this news item?'),
      onConfirm: async () => {
        try {
          await deleteApi(`admin/news-delete/${id}`);
          showToast('success', t('adminNews.deleteSuccess', 'News item deleted successfully.'));
          setNewsData((prevState) => ({
            ...prevState,
            data: prevState.data.filter((item) => item.id !== id),
          }));
        } catch (error) {
          showToast('error', error.message || t('adminNews.deleteError', 'Failed to delete news item.'));
        }
      },
      onCancel: () => console.log('Delete canceled'),
    });
  };

  useEffect(() => {
    fetchNews(currentPage);
  }, [currentPage]);

  const handlePageChange = (page) => setCurrentPage(page);
  const totalPages = Math.ceil(newsData.total / newsData.per_page);

  return (
    <div className="container py-5">
      <div className="com-header d-flex justify-content-between align-items-center mb-4">
        <h2 className="text-primary">{t('adminNews.heading', 'News List')}</h2>
        <Link
          to="/admin/news/create"
          className="btn btn-secondary py-2 px-4 fw-semibold rounded-pill"
        >
          {t("adminNews.createNews", "Create News")}
        </Link>
      </div>

      {loading ? (
        <Loader />
      ) : newsData.data.length > 0 ? (
        <>
          <div className="row">
            {newsData.data.map((news) => (
              <div className="col-md-12 mb-4" key={news.id}>
                <div className="card border-0 shadow-lg rounded-4 overflow-hidden hover-shadow-sm transition-all">
                  <div className="row g-0">
                    <div className="col-lg-4">
                      {news.image ? (
                        <img
                          src={`${baseUrl}${news.image}`}
                          alt={news.title}
                          className="img-fluid w-100 h-100"
                          style={{
                            maxHeight: "220px",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <div className="d-flex align-items-center justify-content-center bg-light text-muted" style={{ height: "220px" }}>
                          {t('adminNews.noImage', 'No image available')}
                        </div>
                      )}
                    </div>

                    <div className="col-lg-8">
                      <div className="card-body p-4">
                        <h4 className="fw-bold text-primary mb-2">{news.title}</h4>
                        {/*<p className="text-muted mb-3">{news.description}</p>*/}
                        <p
  className="text-muted mb-3"
  style={{
    overflow: 'hidden',
    width: '100%',
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    minHeight: '73px',
  }}
  dangerouslySetInnerHTML={{ __html: news.description }}
></p>

                        {news.video_link && (
                          <p className="mb-3">
                            <strong>{t('adminNews.videoLink', 'Video Link:')}</strong>{' '}
                            <a
                              href={news.video_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-decoration-none text-success fw-semibold"
                            >
                              {t('adminNews.watchVideo', 'Watch Video')}
                            </a>
                          </p>
                        )}

                        <div className="d-flex flex-wrap gap-2">
                          <button
                            className="btn btn-danger btn-sm px-3 rounded-pill"
                            onClick={() => handleDelete(news.id)}
                          >
                            <i className="bi bi-trash me-1"></i>
                            {t('adminNews.delete', 'Delete')}
                          </button>

                          <button
                            className="btn btn-outline-primary btn-sm px-3 rounded-pill"
                            onClick={() =>
                              navigate(`/admin/news/edit/${news.id}`, { state: { news } })
                            }
                          >
                            <i className="bi bi-pencil me-1"></i>
                            {t('adminNews.edit', 'Edit')}
                          </button>

                          <button
                            className="btn btn-primary btn-sm px-3 rounded-pill"
                            onClick={() =>
                              navigate(`/admin/news/${news.id}`, { state: { news } })
                            }
                          >
                            <i className="bi bi-eye me-1"></i>
                            {t('adminNews.viewDetails', 'View Details')}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <Pagination
            currentPage={newsData.current_page}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </>
      ) : (
        <p>{t('adminNews.noNews', 'No news found.')}</p>
      )}
    </div>
  );
};

export default AdminBlogsList;